import { useMemo } from 'react';
import { useMediaQuery } from '@mui/material';

/** Phone / narrow viewport — mirrors `useIsMobile` in the agent app. */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 768px)');
}

/** iPad-sized viewport. */
export function useIsIPad(): boolean {
  return useMediaQuery('(min-width: 768px) and (max-width: 1180px)');
}

/** 12.9" iPad Pro (both orientations). */
export function useIsIPadPro129(): boolean {
  return useMediaQuery(
    '((min-width: 1024px) and (max-width: 1366px) and (min-height: 1024px) and (max-height: 1366px))',
  );
}

/** Tablet landscape — used for tower-view scroll height tuning in the agent app. */
export function useIsTabletLandscape(): boolean {
  return useMediaQuery('(min-width:900px) and (max-width:1366px) and (orientation: landscape)');
}

/**
 * Mobile-optimized card styles — strips expensive shadows / blur on phones.
 * Replicated from `TopViewingSitePlanDetail/mobile-hooks.ts`.
 */
export function useMobileOptimizedStyles() {
  const isMobile = useIsMobile();

  return useMemo(
    () => ({
      boxShadow: isMobile ? 'none' : '0 4px 20px rgba(0,0,0,0.15)',
      borderRadius: isMobile ? 1 : 2,
      backdropFilter: isMobile ? 'none' : 'blur(10px)',
      transition: isMobile ? 'all 150ms ease' : 'all 250ms ease',
      ...(isMobile
        ? {
            transform: 'translateZ(0)',
            willChange: 'transform',
          }
        : {}),
    }),
    [isMobile],
  );
}

/**
 * Scroll-container tuning for touch devices. Replicated from the agent app.
 */
export function useScrollableContainerStyles() {
  const isMobile = useIsMobile();

  return useMemo(
    () => ({
      transform: 'translateZ(0)',
      willChange: 'transform',
      contain: 'layout style paint',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: isMobile ? 'none' : 'auto',
      '&::-webkit-scrollbar': isMobile ? { display: 'none' } : {},
      scrollbarWidth: isMobile ? 'none' : 'auto',
    }),
    [isMobile],
  );
}
